import {
  Box,
  Button,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { UseFormReturn } from "react-hook-form";
import { PropsWithChildren } from "react";
import { convertTime, dateToFormalLongString } from "helpers/time";
import { Text14 } from "components/Text";
import { PlusCirlceButtonWithRef } from "components/Button/PlusCircleButton";
import TimeSelectField from "./TimeSelectField";
import DateSelectField from "./DateSelectField";
import TimezoneSelectField from "./TimezoneSelectField";

interface FormWrapperProps extends PropsWithChildren {
  onClose: () => void;
}

function FormWrapper({ children, onClose }: FormWrapperProps) {
  return (
    <Stack spacing="0">
      {children}
      <Button onClick={onClose}>Close</Button>
    </Stack>
  );
}

interface DateTimeZoneFormProps {
  form: UseFormReturn<any>;
  datetimeFieldName: string;
  label?: string;
  disabled?: boolean;
  includeAdd?: boolean;
  buttonVariant?: string;
}

export default function DateTimeZoneForm({
  datetimeFieldName, form, label, disabled = false, includeAdd = true, buttonVariant = "unstyled", ...rest
}: DateTimeZoneFormProps) {
  const { isOpen, onClose, getDisclosureProps, getButtonProps } = useDisclosure();

  const buttonProps = getButtonProps();
  const disclosureProps = getDisclosureProps();
  const bg = useColorModeValue("creamBg.500", "gray.600");
  const { getValues } = form;
  const timezoneFieldName = "timezone";
  const dateFieldName = `local.${datetimeFieldName}.date`;
  const timeFieldName = `local.${datetimeFieldName}.timeOfDay`;

  const [localDate, localTimeOfDay, localTimezone] = getValues([dateFieldName, timeFieldName, timezoneFieldName]);
  const hasValue = !localDate || !localTimeOfDay || !localTimezone;
  if ((!hasValue && disabled) || (!hasValue && !includeAdd)) {
    return null;
  }
  return disabled ? (
    <Text14>{`${dateToFormalLongString(localDate)} at ${convertTime(localTimeOfDay)} (${localTimezone})`}</Text14>
  ) : (
    <Popover matchWidth {...disclosureProps} onClose={onClose} closeOnBlur={false}>
      <PopoverTrigger>
        <Box as="span">
          {localDate ? (
            <Button
              variant={buttonVariant}
              disabled={disabled}
              fontWeight="normal"
              height="32px !important"
              paddingTop="0 !important"
              color="valueBlack.DEFAULT"
              justifyContent="flex-start"
              minW="unset"
              w="auto"
              paddingX="8px !important"
              _hover={{
                bg: "editableBackgroundGrey.light",
              }}
              cursor={disabled ? "normal" : "pointer"}
              {...buttonProps}
            >
              {`${dateToFormalLongString(localDate)} at ${convertTime(localTimeOfDay)} (${localTimezone})`}
            </Button>
          ) : (
            <PlusCirlceButtonWithRef label={label ? `Add ${label}` : "Add"} {...buttonProps} />
          )}
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent bg={bg} w="400px" display={isOpen ? "block" : "none"}>
          <PopoverBody>
            <FormWrapper onClose={onClose}>
              <DateSelectField
                datetimeFieldName={datetimeFieldName}
                label="Send on"
                form={form}
              />
              <TimeSelectField
                datetimeFieldName={datetimeFieldName}
                label="Send at"
                form={form}
                sx={{ textIndent: "3px !important" }}
              />
              <TimezoneSelectField
                datetimeFieldName={datetimeFieldName}
                form={form}
              />
            </FormWrapper>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
